import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/app/store/hooks';
import { selectDisplayFormat } from '@/app/store/selectedEntitySlice';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationPanel } from './notifications/NotificationPanel';
import type { NotificationRow } from './notifications/notificationGroups';

interface HeaderNotificationsProps {
  /** Whether this role has a notification feed at all; see useHeader's note on who does. */
  enabled: boolean;
}

/**
 * The bell, its unread badge, and the panel behind it.
 *
 * <p>The panel was deferred when the bell was built (niraxobid.md section 10.1) because nothing wrote to a
 * feed. Background master imports now do, and approvals and BID events follow the same path — any module
 * that raises an `InAppNotification` lands here with no change on this side.
 *
 * <p>Clicking a notification marks it read and navigates to the record it is about, using the link the
 * server returns from the mark rather than one cached when the panel opened.
 */
export function HeaderNotifications({ enabled }: HeaderNotificationsProps) {
  const {
    items,
    counts,
    tab,
    isOpen,
    loading,
    failed,
    markingAll,
    selectTab,
    close,
    toggle,
    openNotification,
    markAllRead,
  } = useNotifications(enabled);
  const navigate = useNavigate();
  /* The entity's zone and formats; null until resolved, which falls back to the platform's own. */
  const displayFormat = useAppSelector(selectDisplayFormat);

  const showBadge = counts.unread > 0;

  const onOpenNotification = (row: NotificationRow) => {
    void openNotification(row.notification).then((link) => {
      if (link === null) return;
      /* Closed first: navigating with the drawer still over the screen hides the record it just opened. */
      close();
      navigate(link);
    });
  };

  return (
    <div className="header__notification-wrap">
      <button
        type="button"
        className="header__notification"
        aria-label={showBadge ? `${String(counts.unread)} unread notifications` : 'Notifications'}
        aria-expanded={isOpen}
        onClick={toggle}
      >
        <i className="pi pi-bell header__bell" aria-hidden="true" />
        {showBadge ? (
          // The count is already in the button's aria-label.
          <span className="header__badge" aria-hidden="true">
            {counts.unread}
          </span>
        ) : null}
      </button>

      {/*
        Portalled to <body>. The header is sticky with its own z-index, which makes it a stacking context: left
        inside it, the drawer could rise no higher than the header itself, and anything on the page stacked
        above the header — the supplier wizard's sticky stepper, for one — drew over both scrim and drawer.
      */}
      {isOpen
        ? createPortal(
            <>
              {/* Catches the click that closes the panel, so anywhere outside it dismisses. */}
              <div className="header__notify-scrim" onClick={close} aria-hidden="true" />
              <NotificationPanel
                items={items}
                counts={counts}
                tab={tab}
                loading={loading}
                failed={failed}
                markingAll={markingAll}
                displayFormat={displayFormat}
                onSelectTab={selectTab}
                onOpenNotification={onOpenNotification}
                onMarkAllRead={markAllRead}
                onClose={close}
              />
            </>,
            document.body,
          )
        : null}
    </div>
  );
}
